import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaEye, FaImage, FaBold, FaItalic, FaUnderline, FaStrikethrough, FaListUl, FaListOl, FaQuoteLeft, FaLink, FaAlignLeft, FaAlignCenter, FaAlignRight, FaHeading, FaUpload, FaUndo, FaRedo } from 'react-icons/fa';
import { supabase } from '../lib/supabase';

const CATEGORIES = ['Prestasi', 'Event', 'Kolaborasi', 'Program', 'Umum'];

const NewsEditor = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const inlineImageInputRef = useRef(null);

  const [title, setTitle] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('Umum');
  const [coverUrl, setCoverUrl] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [authorName, setAuthorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeFormats, setActiveFormats] = useState({});

  // Fetch existing article for editing
  useEffect(() => {
    if (!isEdit) return;
    supabase.from('news').select('*').eq('id', parseInt(id, 10)).single()
      .then(({ data, error }) => {
        if (error || !data) { setErrorMessage('Artikel tidak ditemukan.'); return; }
        setTitle(data.title || '');
        setExcerpt(data.excerpt || '');
        setCategory(data.category || 'Umum');
        setCoverUrl(data.cover_url || '');
        setCoverPreview(data.cover_url || '');
        setIsPublished(data.is_published || false);
        setAuthorName(data.author_name || '');
        // Set editor content after mount
        setTimeout(() => {
          if (editorRef.current) editorRef.current.innerHTML = data.content || '';
        }, 100);
      })
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  // Track active formatting
  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
    });
  }, []);

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    updateActiveFormats();
  };

  const handleCoverFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const insertImage = () => {
    // Trigger hidden file input
    inlineImageInputRef.current?.click();
  };

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be selected again

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `news-inline-${Date.now()}.${fileExt}`;
      const filePath = `alumni-photos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('alumni-photos')
        .upload(filePath, file, { contentType: file.type, cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('alumni-photos').getPublicUrl(filePath);
      const publicUrl = urlData.publicUrl;

      // Insert image at cursor position
      editorRef.current?.focus();
      document.execCommand('insertImage', false, publicUrl);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Gagal upload gambar: ' + (err.message || 'Coba lagi.'));
    } finally {
      setUploadingImage(false);
    }
  };

  const insertLink = () => {
    const url = prompt('Masukkan URL link:');
    if (url) exec('createLink', url);
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 100)
      + '-' + Date.now().toString(36);
  };

  const handleSubmit = async (publishNow = null) => {
    setLoading(true);
    setErrorMessage('');

    if (!title.trim()) {
      setErrorMessage('Judul artikel wajib diisi.');
      setLoading(false);
      return;
    }

    const content = editorRef.current?.innerHTML || '';
    if (!content.trim() || content === '<br>') {
      setErrorMessage('Konten artikel wajib diisi.');
      setLoading(false);
      return;
    }

    try {
      let finalCoverUrl = coverUrl;

      // Upload cover image if a new file is selected
      if (coverFile) {
        const fileExt = coverFile.name.split('.').pop();
        const fileName = `news-cover-${Date.now()}.${fileExt}`;
        const filePath = `alumni-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('alumni-photos')
          .upload(filePath, coverFile, { contentType: coverFile.type, cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('alumni-photos').getPublicUrl(filePath);
        finalCoverUrl = urlData.publicUrl;
      }

      const published = publishNow !== null ? publishNow : isPublished;

      const payload = {
        title: title.trim(),
        excerpt: excerpt.trim() || null,
        content,
        cover_url: finalCoverUrl || null,
        category,
        is_published: published,
        author_name: authorName.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (isEdit) {
        const { error } = await supabase.from('news').update(payload).eq('id', parseInt(id, 10));
        if (error) throw error;
      } else {
        payload.slug = generateSlug(title);
        const { error } = await supabase.from('news').insert([payload]);
        if (error) throw error;
      }

      alert(isEdit ? 'Artikel berhasil diperbarui!' : 'Artikel berhasil disimpan!');
      navigate('/admin/news');
    } catch (error) {
      console.error('Error saving article:', error);
      setErrorMessage('Gagal menyimpan: ' + (error.message || 'Silakan coba lagi.'));
    } finally {
      setLoading(false);
    }
  };

  const toolbarBtn = (icon, command, label) => (
    <button
      key={command}
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(command); }}
      className={`p-2 rounded-lg transition text-sm ${activeFormats[command] ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
      title={label}
    >
      {icon}
    </button>
  );

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin/news" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-1 mb-3 transition-colors">
            <FaArrowLeft /> Kembali ke Daftar Berita
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {isEdit ? 'Edit Artikel' : 'Tulis Artikel Baru'}
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main editor area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Judul artikel..."
                className="w-full px-0 py-3 text-3xl font-bold text-gray-900 placeholder-gray-300 border-0 border-b-2 border-gray-200 focus:outline-none focus:border-primary-500 transition bg-transparent"
              />
            </div>

            {/* Excerpt */}
            <div>
              <textarea
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                placeholder="Tulis ringkasan singkat (opsional, muncul di daftar berita)..."
                rows={2}
                className="w-full px-0 py-2 text-gray-500 placeholder-gray-300 border-0 border-b border-gray-100 focus:outline-none focus:border-primary-300 transition bg-transparent text-sm resize-none"
              />
            </div>

            {/* Rich Text Toolbar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-100 px-3 py-2 flex flex-wrap items-center gap-0.5">
                {/* Text formatting */}
                {toolbarBtn(<FaBold />, 'bold', 'Bold')}
                {toolbarBtn(<FaItalic />, 'italic', 'Italic')}
                {toolbarBtn(<FaUnderline />, 'underline', 'Underline')}
                {toolbarBtn(<FaStrikethrough />, 'strikeThrough', 'Strikethrough')}

                <div className="w-px h-6 bg-gray-200 mx-1"></div>

                {/* Headings */}
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h2'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm font-bold" title="Heading 2">
                  H2
                </button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h3'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm font-bold" title="Heading 3">
                  H3
                </button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'p'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-xs" title="Paragraph">
                  <FaHeading />
                </button>

                <div className="w-px h-6 bg-gray-200 mx-1"></div>

                {/* Lists */}
                {toolbarBtn(<FaListUl />, 'insertUnorderedList', 'Bullet List')}
                {toolbarBtn(<FaListOl />, 'insertOrderedList', 'Numbered List')}
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'blockquote'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Quote">
                  <FaQuoteLeft />
                </button>

                <div className="w-px h-6 bg-gray-200 mx-1"></div>

                {/* Alignment */}
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('justifyLeft'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Align Left"><FaAlignLeft /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('justifyCenter'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Center"><FaAlignCenter /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('justifyRight'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Align Right"><FaAlignRight /></button>

                <div className="w-px h-6 bg-gray-200 mx-1"></div>

                {/* Insert */}
                <button type="button" onMouseDown={e => { e.preventDefault(); insertLink(); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Insert Link"><FaLink /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); insertImage(); }} className={`p-2 rounded-lg transition text-sm ${uploadingImage ? 'text-primary-500 animate-pulse' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`} title="Upload Gambar" disabled={uploadingImage}>{uploadingImage ? <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin"></div> : <FaImage />}</button>
                <input type="file" accept="image/*" ref={inlineImageInputRef} onChange={handleInlineImageUpload} className="hidden" />

                <div className="w-px h-6 bg-gray-200 mx-1"></div>

                {/* Undo / Redo */}
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('undo'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Undo"><FaUndo /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('redo'); }} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition text-sm" title="Redo"><FaRedo /></button>
              </div>

              {/* Editor area */}
              <div
                ref={editorRef}
                contentEditable
                onKeyUp={updateActiveFormats}
                onMouseUp={updateActiveFormats}
                className="min-h-[400px] px-6 py-5 focus:outline-none prose prose-sm max-w-none
                  [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-3
                  [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2
                  [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-3
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                  [&_li]:text-gray-700 [&_li]:mb-1
                  [&_blockquote]:border-l-4 [&_blockquote]:border-primary-400 [&_blockquote]:bg-primary-50 [&_blockquote]:px-4 [&_blockquote]:py-3 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-4 [&_blockquote]:rounded-r-lg
                  [&_a]:text-primary-600 [&_a]:underline
                  [&_img]:rounded-xl [&_img]:max-w-full [&_img]:my-4 [&_img]:shadow-md
                  [&_code]:bg-gray-100 [&_code]:text-red-600 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm"
                data-placeholder="Mulai menulis artikel disini..."
                style={{ minHeight: '400px' }}
                suppressContentEditableWarning
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-primary-500 to-gold-500"></div>
              <div className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-900">Publikasi</h3>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-60 text-sm"
                  >
                    {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <FaEye />}
                    {isEdit ? 'Update & Publish' : 'Publish Sekarang'}
                  </button>
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-xl transition disabled:opacity-60 text-sm"
                  >
                    <FaSave /> Simpan Draft
                  </button>
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
              <label className={labelClass}>Kategori</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className={inputClass}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Author */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
              <label className={labelClass}>Penulis <span className="text-gray-400 font-normal">(Opsional)</span></label>
              <input
                type="text"
                value={authorName}
                onChange={e => setAuthorName(e.target.value)}
                placeholder="Nama penulis"
                className={inputClass}
              />
            </div>

            {/* Cover Image */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
              <label className={labelClass}>Gambar Cover</label>
              {coverPreview && (
                <div className="relative mb-3">
                  <img src={coverPreview} alt="Cover" className="w-full h-40 object-cover rounded-xl" />
                  <button
                    type="button"
                    onClick={() => { setCoverFile(null); setCoverPreview(''); setCoverUrl(''); }}
                    className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full text-xs flex items-center justify-center hover:bg-red-600 transition"
                  >
                    ×
                  </button>
                </div>
              )}
              <label className="cursor-pointer flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-4 hover:border-primary-400 hover:bg-primary-50 transition text-sm text-gray-500 hover:text-primary-600">
                <FaUpload /> Upload Cover
                <input type="file" accept="image/*" onChange={handleCoverFile} className="hidden" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsEditor;
